import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { VariantProps, cva } from "class-variance-authority"

import { cn } from "@/utils/classname"
import { analytics } from "@/telemetry/amplitude"
import { ContentTooltip } from "./ContentTooltip"
import { toTestId } from "./testid"
import { PopoverContentProps } from "@radix-ui/react-popover"
import { useEffect } from "react"

const buttonVariants = cva(
	"electron-no-drag inline-flex shrink-0 cursor-default items-center justify-center rounded-md text-sm transition-colors hover:bg-opacity-50 focus:outline-none active:bg-opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
	{
		variants: {
			variant: {
				default:
					"border-DEFAULT border-solid border-base-50/10 bg-base-600 font-semibold text-base-100",
				outline:
					"border border-solid border-base-50/10 bg-transparent font-semibold text-base-100 hover:bg-base-100/10",
				primary:
					"border-DEFAULT border-solid border-base-50/10 bg-accent-600 font-semibold text-base-50",
				destructive:
					"border-DEFAULT border-solid border-base-50/10 bg-red-500 font-semibold text-base-50",
				text: "border-0 bg-transparent font-semibold text-base-300/50 hover:text-base-300 focus:text-base-300/100",
				ghost: "border-0 bg-transparent font-semibold hover:text-base-300 focus:text-base-300/75",
				icon: "mx-1 size-7 flex-col justify-center bg-base-800 text-xxs font-semibold hover:bg-base-700",
				custom: "",
			},
			size: {
				default: "h-7 rounded-md px-4 text-sm",
				sm: "h-[25px] rounded-md px-2 text-xs",
				custom: "",
			},
			isSelected: {
				default: "",
				true: "bg-base-700 text-base-100 hover:bg-base-700 dark:bg-base-300 dark:text-base-800 dark:hover:bg-base-300",
				false: "bg-base-800 text-base-300",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
			isSelected: "default",
		},
	},
)

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLDivElement>, VariantProps<typeof buttonVariants> {
	asChild?: boolean
	isSelected?: boolean
	selectedClass?: string
	disableEnterKey?: boolean
	trackingLabel: string
	trackingValue?: string
	trackingValues?: Record<string, unknown> | (() => Record<string, unknown> | undefined)
	tooltip?: string
	tooltipDelay?: number //in ms
	tooltipProps?: PopoverContentProps
	tooltipMaxWidth?: number
}

const Button = React.forwardRef<HTMLDivElement, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			isSelected,
			selectedClass,
			disableEnterKey,
			asChild = false,
			onKeyDown,
			onClick,
			trackingLabel,
			trackingValue,
			trackingValues,
			tooltip,
			tooltipDelay,
			tooltipMaxWidth,
			disabled,
			...props
		},
		ref,
	) => {
		const Comp = asChild ? Slot : "div"

		const handleKeyDown =
			disableEnterKey || onKeyDown
				? (e: React.KeyboardEvent<HTMLDivElement>) => {
						if (disabled) return
						disableEnterKey && e.key === "Enter" && e.preventDefault()
						onKeyDown?.(e)
					}
				: undefined

		const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
			if (disabled) return
			onClick?.(e)
			const resolvedTrackingValues =
				typeof trackingValues === "function" ? trackingValues() : trackingValues
			analytics.trackEvent(
				{
					type: "click",
					label: trackingLabel,
					value: trackingValue,
					customProperties: resolvedTrackingValues,
				},
				e.target,
			)
		}

		const [showTooltip, setShowTooltip] = React.useState(false)
		const [showTooltipTimeout, setTooltipTimeout] = React.useState<NodeJS.Timeout | null>(null)

		const foregroundClass =
			variant === "primary" || variant === "destructive"
				? "text-base-950 dark:text-base-50"
				: undefined
		const applySelectedClass = Boolean(isSelected && selectedClass)
		const variantSelectedState = applySelectedClass ? undefined : isSelected
		const mergedButtonClassName = cn(
			buttonVariants({ variant, size, isSelected: variantSelectedState }),
			className,
			foregroundClass,
			applySelectedClass && selectedClass,
		)

		const handleMouseEnter = () => {
			setTooltipTimeout(
				setTimeout(() => {
					setShowTooltip(true)
				}, tooltipDelay ?? 500),
			)
		}

		const handleMouseLeave = () => {
			showTooltipTimeout && clearTimeout(showTooltipTimeout)
			setTooltipTimeout(null)
			setShowTooltip(false)
		}

		// Clear timeout on unmount
		useEffect(() => {
			return () => {
				if (showTooltipTimeout) clearTimeout(showTooltipTimeout)
			}
		}, [showTooltipTimeout])

		// When disabled with a tooltip, wrap in a div to capture hover events
		if (disabled && tooltip) {
			// Check if w-full is in the className to preserve full width
			const isFullWidth = className?.includes("w-full")
			return (
				<ContentTooltip open={showTooltip} tooltip={tooltip}>
					<div
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
						className={isFullWidth ? "flex w-full" : "inline-flex"}
					>
						<Comp
							role="button"
							tabIndex={-1}
							aria-label={tooltip ?? trackingLabel}
							aria-disabled={true}
							aria-pressed={
								isSelected === true
									? true
									: isSelected === false
										? false
										: undefined
							}
							className={cn(
								mergedButtonClassName,
								"pointer-events-none cursor-not-allowed opacity-50",
							)}
							data-button-variant={variant}
							data-primary-action={variant === "primary" ? "true" : undefined}
							data-disable-enter-key={disableEnterKey ? "true" : undefined}
							data-testid={toTestId(trackingLabel)}
							ref={ref}
							{...props}
						/>
					</div>
				</ContentTooltip>
			)
		}

		return (
			<ContentTooltip
				open={showTooltip}
				tooltip={tooltip}
				tooltipMaxWidth={tooltipMaxWidth}
				{...props.tooltipProps}
			>
				<Comp
					role="button"
					tabIndex={disabled ? -1 : 0}
					aria-label={tooltip ?? trackingLabel}
					aria-disabled={disabled || undefined}
					aria-pressed={
						isSelected === true ? true : isSelected === false ? false : undefined
					}
					className={cn(
						mergedButtonClassName,
						disabled && "pointer-events-none cursor-not-allowed opacity-50",
					)}
					data-button-variant={variant}
					data-primary-action={variant === "primary" ? "true" : undefined}
					data-disable-enter-key={disableEnterKey ? "true" : undefined}
					data-testid={toTestId(trackingLabel)}
					onKeyDown={handleKeyDown}
					ref={ref}
					onClick={handleClick}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					{...props}
				/>
			</ContentTooltip>
		)
	},
)
Button.displayName = "Button"

export { Button, buttonVariants }
